import React from 'react';
import { Box, Paper, Typography, Grid, Rating, Button, Stack } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import IntensityTimeline from "./IntensityTimeline";
import { PubObject, HeatmapData } from "./FindPubs";
import { themeColors } from '../styles/theme';
import { PrimaryButton } from "./Home";
import { SunIcon } from "./SunIcon";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VerifiedBadge from './VerifiedBadge';

interface PubCardProps {
  pub: PubObject;
  timelineMode: 'full' | 'compact';
  raycastLoadingForThisPub: boolean; // True if raycasting for this specific pub is in progress
  onViewPub: (pub: PubObject) => void;
  onSubmitGarden: (pub: PubObject) => void;
  is3DSupported: boolean | null; // Global 3D support status from parent component
}

const PubCard: React.FC<PubCardProps> = ({
  pub,
  timelineMode,
  raycastLoadingForThisPub,
  onViewPub,
  onSubmitGarden,
  is3DSupported,
}) => {
  // Check if this specific pub or the entire area is not 3D supported
  const isPubNot3DSupported = pub.is_3d_supported === false || is3DSupported === false;

  const handleCardClick = () => {
    // Only allow click if 3D is supported or we don't know yet
    if (!isPubNot3DSupported) {
      onViewPub(pub);
    }
  };

  const handleSubmitOrUpdateGardenClick = (e: React.MouseEvent) => {
    // Only allow garden submission if 3D is supported
    if (!isPubNot3DSupported) {
      e.stopPropagation();
      onSubmitGarden(pub);
    }
  };

  const renderIntensityTimeline = (
    heatmap: HeatmapData | undefined,
    id: string,
    isDataExpected: boolean // True if this timeline instance is for a garden/area expected to have data
  ) => {
    const heatmapAvailable = heatmap && heatmap.timestamps && heatmap.timestamps.length > 0 && heatmap.values && Object.keys(heatmap.values).length > 0;
  
    let showLoadingState: boolean;
  
    if (isDataExpected) {
      // If heatmap data is not yet available, it's in a loading state (either fetching or about to fetch).
      // Also, if data is available but an explicit re-fetch for this pub is active (raycastLoadingForThisPub is true),
      // still show loading.
      if (!heatmapAvailable || raycastLoadingForThisPub) {
        showLoadingState = true;
      } else {
        // Data is available, and no active raycasting for this pub.
        showLoadingState = false;
      }
    } else {
      // No data is expected for this timeline instance (e.g., garden_drawn is false for main pub, no garden array).
      showLoadingState = false;
    }
  
    const timelineDataToPass = heatmapAvailable
      ? { timestamps: heatmap.timestamps, values: { [id]: heatmap.values } } // Pass values for the specific ID
      : { timestamps: [], values: {} }; // Pass empty structure if no data / loading
  
    // Render the timeline if data is available OR if it's determined to be in a loading state.
    if (heatmapAvailable || showLoadingState) {
      return (
        <IntensityTimeline
          data={timelineDataToPass}
          loading={showLoadingState} // CRITICAL: Pass the determined loading state
          googlePlaceId={id} // This should be the key used in timelineDataToPass.values
          sunriseTime={pub.sunrise}
          sunsetTime={pub.sunset}
          mode={timelineMode}
          duskDawnDurationMinutes={30}
        />
      );
    }
  
    // If not heatmapAvailable and not showLoadingState, then display "No sunlight data"
    return (
      <Box
        sx={{
          height: '110px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: '0.8rem',
        }}
      >
        No sunlight data available
      </Box>
    );
  };


  return (
    <Paper
      elevation={2}
      sx={{
        display: 'flex',
        backgroundColor: isPubNot3DSupported ? themeColors.lightBlue : 
                        pub.has_no_garden ? themeColors.lightBlue : themeColors.paperYellow,
        backdropFilter: 'blur(16px)',
        color: 'black',
        position: 'relative',
        margin: pub.has_no_garden || isPubNot3DSupported
          ? { xs: '8px 0', sm: '10px 8px', md: '10px 16px' }
          : { xs: '12px 0', sm: '16px 8px', md: '20px 16px' },
        borderRadius: 4,
        outline: 'none',
        cursor: isPubNot3DSupported ? 'default' : 'pointer',
        boxShadow: pub.has_no_garden || isPubNot3DSupported
          ? '0 2px 5px rgba(0,0,0,0.05)'
          : '0 3px 10px rgba(0,0,0,0.08)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: isPubNot3DSupported ? 'none' : 
                     pub.has_no_garden ? 'translateY(-2px)' : 'translateY(-3px)',
          boxShadow: isPubNot3DSupported ? '0 2px 5px rgba(0,0,0,0.05)' :
                     pub.has_no_garden ? '0 4px 8px rgba(0,0,0,0.1)' : '0 6px 15px rgba(0,0,0,0.15)',
        },
        padding: '24px'
      }}
      onClick={isPubNot3DSupported ? undefined : handleCardClick}
    >
      {isPubNot3DSupported ? (
        <Grid container sx={{ alignItems: 'center', width: '100%' }}>
          <Grid item xs={12}>
            <Typography sx={{ fontWeight: '700', fontSize: '28px', color: themeColors.darkBackground, fontFamily: "'DM Sans', sans-serif" }}>
              {pub.name.toUpperCase()}
            </Typography>
            <Typography sx={{ color: '#888', fontWeight: 400, fontSize: '0.85rem', fontFamily: "'DM Sans', sans-serif", mt: 0.25 }}>
              {Math.round(pub.distance)}m
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, color: '#666' }}>
              <InfoOutlinedIcon sx={{ fontSize: '1rem', mr: 1, color: themeColors.blue }} />
              <Typography sx={{ fontSize: '0.9rem', color: themeColors.blue, fontFamily: "'DM Sans', sans-serif" }}>
                3D data not available for this location
              </Typography>
            </Box>
          </Grid>
        </Grid>
      ) : pub.has_no_garden ? (
        <Grid container sx={{ alignItems: 'center', width: '100%' }}>
          <Grid item xs={7} sm={9}>
            <Typography sx={{ fontWeight: '700', fontSize: '28px', color: themeColors.darkBackground, fontFamily: "'DM Sans', sans-serif" }}>
              {pub.name.toUpperCase()}
            </Typography>
            <Typography sx={{ color: '#888', fontWeight: 400, fontSize: '0.85rem', fontFamily: "'DM Sans', sans-serif", mt: 0.25 }}>
              NO GARDEN • {Math.round(pub.distance)}m
            </Typography>
          </Grid>
          <Grid item xs={5} sm={3} sx={{ textAlign: 'right' }}>
            <PrimaryButton
              variant="text"
              size="small"
              onClick={handleSubmitOrUpdateGardenClick}
              sx={{ color: 'black', fontWeight: 400, fontSize: '0.8rem', minWidth: 'auto', '&:hover': { background: 'transparent', opacity: 0.8 }, padding: '4px' }}
            >
              UPDATE
            </PrimaryButton>
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ width: '100%' }}>
          <Box>
            <Typography sx={{ fontWeight: '700', fontSize: '30px', color: themeColors.darkBackground, fontFamily: "'DM Sans', sans-serif" }}>
              {pub.name.toUpperCase()}
            </Typography>
            {pub.garden_moderated && <VerifiedBadge sx={{ mt: 1 }} />}
          </Box>

          <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-start' }}>
            {pub.rating ? (
              <Rating
                name={`rating-desktop-${pub.google_place_id}`}
                value={pub.rating ?? 0}
                readOnly
                icon={<SunIcon fontSize="inherit" sx={{ color: '#FFD700' }} />}
                emptyIcon={<></>}
                sx={{ '& .MuiRating-icon': { marginRight: '2px' } }}
              />
            ) : (
              <Typography variant="body2" sx={{ color: '#757575', fontStyle: 'italic' }}>No rating yet</Typography>
            )}
          </Box>

          <Grid container sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'space-between', alignItems: 'center', mt: 0.5, mb: 0.5 }}>
            <Grid item>
              {pub.rating ? (
                <Rating
                  name={`rating-mobile-${pub.google_place_id}`}
                  value={pub.rating ?? 0}
                  readOnly
                  icon={<SunIcon fontSize="inherit" sx={{ color: '#FFD700' }} />}
                  emptyIcon={<></>}
                  sx={{ '& .MuiRating-icon': { marginRight: '2px' } }}
                />
              ) : (
                <Typography variant="body2" sx={{ color: '#757575', fontStyle: 'italic' }}>No rating yet</Typography>
              )}
            </Grid>
            <Grid item>
              <Typography sx={{ fontSize: '0.9rem', color: themeColors.darkBackground, fontFamily: "'DM Sans', sans-serif", textAlign: 'right' }}>
                {`${Math.round(pub.distance)} meters`}
              </Typography>
            </Grid>
          </Grid>
          
          <Grid item xs={8} sm={8} sx={{ color: 'black', fontWeight: 500, fontSize: '0.9rem', fontFamily: "'DM Sans', sans-serif" }}>
            {!pub.garden_drawn && !(pub.gardens && pub.gardens.length > 0) ? 'Garden not drawn'.toUpperCase() : ''}
          </Grid>
          
          <Grid item xs={4} sm={4} sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '0.9rem', color: themeColors.darkBackground, fontFamily: "'DM Sans', sans-serif" }}>
            {`${Math.round(pub.distance)} meters`}
          </Grid>

          {pub.gardens && pub.gardens.length > 0 ? (
            <Grid item xs={12}>
              {pub.gardens.map((garden, index) => (
                <Box key={garden.uid} sx={{ mb: index < pub.gardens!.length - 1 ? 2 : 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', color: 'black', mt: 2 }}>
                      {garden.name.startsWith(`${pub.name} - `) ? garden.name.slice(pub.name.length + 3).toUpperCase() : garden.name.toUpperCase()}
                    </Typography>
                    {garden.is_uncertain && <HelpOutlineIcon sx={{ ml: 1, fontSize: '1rem', color: themeColors.orange }} />}
                  </Box>
                  <Box sx={{ background: 'transparent', height: '110px', width: '100%', padding: '5px', border: 'none', borderRadius: '0' }}>
                    {renderIntensityTimeline(garden.heatmap, garden.uid, true)}
                  </Box>
                  {garden.is_uncertain && (
                    <Box sx={{ mt: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ color: themeColors.orange, fontSize: '0.85rem' }}>
                        We're not sure about this garden's location.
                      </Typography>
                      <Button
                        variant="contained" size="small" onClick={handleSubmitOrUpdateGardenClick}
                        sx={{ mt: 0.5, background: themeColors.lightBlue, color: themeColors.blue, fontWeight: 500, boxShadow: 'none', fontFamily: "'DM Sans', sans-serif", borderRadius: '12px', '&:hover': { background: themeColors.lightBlue, opacity: 0.9, boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)' } }}
                      >
                        Help clarify this garden
                      </Button>
                    </Box>
                  )}
                </Box>
              ))}
            </Grid>
          ) : ( // This case handles when pub.gardens is undefined or empty
            <>
              {/* Show timeline for main pub if garden_drawn is true OR if raycasting is happening for it */}
              { (pub.garden_drawn || raycastLoadingForThisPub) && (
                 <Grid item xs={12}>
                    <Box sx={{ mt: 1, height: '120px', width: '100%', backgroundColor: 'transparent', borderRadius: '0', overflow: 'hidden' }}>
                        {/* For the main pub's timeline, data is expected if pub.garden_drawn is true.
                            Pass pub.google_place_id as the ID for IntensityTimeline. */}
                        {renderIntensityTimeline(pub.heatmap, pub.google_place_id, !!pub.garden_drawn)}
                    </Box>
                 </Grid>
              )}
            </>
          )}

          {/* Show "Submit a Garden" button if no gardens are drawn for this pub */}
          {(!pub.garden_drawn && (!pub.gardens || pub.gardens.length === 0)) && (
            <Grid item xs={12}>
              <Button
                variant="contained" size="small" onClick={handleSubmitOrUpdateGardenClick}
                sx={{ mt: 1.5, width: '100%', background: themeColors.lightBlue, color: themeColors.blue, fontWeight: 500, boxShadow: 'none', fontFamily: "'DM Sans', sans-serif", borderRadius: '12px', '&:hover': { background: themeColors.lightBlue, opacity: 0.9, boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)' } }}
              >
                Submit a Garden for this Pub
              </Button>
            </Grid>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default React.memo(PubCard);